import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass,
  Heartbeat,
  Lightbulb,
  Handshake,
  RocketLaunch,
} from "@phosphor-icons/react";
import { SITE } from "@/lib/site";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import { PAGE_SEO } from "@/lib/seo";

const VALUES = [
  {
    icon: Compass,
    title: "Product over project",
    body: "We build products, not billable projects. Every hour we ship is an hour of value for every one of our customers — not a report for one.",
  },
  {
    icon: Heartbeat,
    title: "Self-serve, always",
    body: "If a customer can't figure out our product in an afternoon, that's a bug we own — not something to fix with a training deck.",
  },
  {
    icon: Lightbulb,
    title: "Opinionated defaults",
    body: "We&apos;d rather ship an opinion that&apos;s 90% right for 90% of teams than a 100 flexible knobs nobody knows how to tune.",
  },
  {
    icon: Handshake,
    title: "One account, many products",
    body: "Everything we build lives in one login, one data model and one design language. Choose one product; the rest are ready when you are.",
  },
];

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <Seo page={PAGE_SEO.about} />
      <p className="sr-only">{PAGE_SEO.about.h1Fact}</p>
      {/* HERO */}
      <section className="bg-[#fff6ec]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h1
              data-testid="about-hero-title"
              className="font-display text-5xl sm:text-6xl lg:text-[4.5rem] font-bold tracking-tight text-[#1f2a44] leading-[1.02]"
            >
              A Platform —<br />
              <span className="text-[#fe4c1c] italic font-light">Where Innovation<br />Never Ends.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-slate-600 leading-relaxed">
              {SITE.name} is a Bangalore-headquartered software product company. We build opinionated, self-serve tools that businesses actually enjoy logging in to — starting with <strong className="text-[#1f2a44] font-semibold">VerifyPro</strong>, our Background Verification platform.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-[#fe4c1c]/10 blur-2xl" aria-hidden />
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                alt="Team collaborating on modern software"
                className="relative rounded-3xl w-full h-[440px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT MARQUEE — moved from Home */}
      <Marquee />

      {/* MISSION / VISION */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div data-testid="mission-block">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">Mission</p>
            <h3 className="mt-4 font-display text-3xl sm:text-4xl font-bold leading-tight text-[#1f2a44]">
              Build software that treats business users like software users.
            </h3>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Business software has, for two decades, forgotten that its users are people too — people who&apos;d rather figure things out for themselves than sit through a demo. We build products for that person.
            </p>
          </div>
          <div data-testid="vision-block">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">Vision</p>
            <h3 className="mt-4 font-display text-3xl sm:text-4xl font-bold leading-tight text-[#1f2a44]">
              One account. A whole suite of products modern businesses can grow into.
            </h3>
            <p className="mt-6 text-slate-600 leading-relaxed">
              VerifyPro is our first product. OpsDesk and more are on the roadmap. All under one login, one billing account, one design language.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 lg:py-28 bg-[#fff6ec] border-y border-[#1f2a44]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">The Infini10 way</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44]">
              Four principles.<br />Every product. Every release.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10 hover:border-[#1f2a44]/30 hover:shadow-xl transition-all"
                >
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1f2a44] text-[#fe4c1c]">
                    <Icon size={26} weight="duotone" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-bold text-[#1f2a44]">{v.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <RocketLaunch size={40} weight="duotone" className="text-[#fe4c1c]" />
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44] leading-[1.1]">
              Start with our first product.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-lg">
              VerifyPro is live. Sign up for a 14-day trial and see what an opinionated, self-serve BGV platform feels like.
            </p>
          </div>
          <div className="lg:justify-self-end">
            <Link
              to="/services"
              data-testid="about-cta-products"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#1f2a44] text-[#fe4c1c] text-sm font-semibold hover:bg-[#152038] transition-colors"
            >
              Explore products <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
